/**
 * Service API campagnes — version publique pour donoor.org.
 *
 * Couche 1 — Transport (ADR-001). Endpoints publics utilisés depuis la
 * page /c/[slug] (détail campagne + liste donateurs paginée).
 *
 * Utilise `apiCall` du data-layer en mode public (skipAuth +
 * skipOrganizationId).
 */

import { apiCall } from "@donoor/data-layer";
import type { Campaign, CampaignDonor, TopDonor } from "~/types/campaign";

const PUBLIC_OPTS = { skipAuth: true, skipOrganizationId: true } as const;

export type DonorSortBy = "recent" | "highest";

export interface CampaignDonorsResponse {
  data: CampaignDonor[];
  pagination: { total: number; limit: number; offset: number };
  topDonor: TopDonor | null;
}

export const campaignsPublicApi = {
  /** Récupère le détail d'une campagne publique par slug. */
  async bySlug(slug: string, signal?: AbortSignal): Promise<Campaign> {
    const res = await apiCall<{ data: Campaign }>(
      `/api/campaigns/public/slug/${slug}`,
      { signal, ...PUBLIC_OPTS },
    );
    return res.data;
  },

  /** Récupère une page de donateurs pour une campagne (trie + offset). */
  donors(
    campaignId: number | string,
    params: { limit: number; offset: number; sortBy: DonorSortBy },
    signal?: AbortSignal,
  ): Promise<CampaignDonorsResponse> {
    return apiCall<CampaignDonorsResponse>(
      `/api/campaigns/public/${campaignId}/donors`,
      {
        query: {
          limit: params.limit,
          offset: params.offset,
          sortBy: params.sortBy,
        },
        signal,
        ...PUBLIC_OPTS,
      },
    );
  },

  /**
   * Incrémente le compteur de vues d'une campagne publique.
   * Fire-and-forget côté UI (non bloquant — analytics uniquement).
   */
  async trackView(slug: string): Promise<void> {
    try {
      await apiCall(`/api/campaigns/public/${slug}/view`, {
        method: "POST",
        body: {},
        ...PUBLIC_OPTS,
      });
    } catch {
      // Non-bloquant : analytics best-effort
    }
  },
};
